import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DoneOutlined from "@mui/icons-material/DoneOutlined";

import FileUploadDialog from './FileUploadDialog';


const SortableItem = ({ id, itemKey, text, iso_code, icon_url, published, listIndex, onEditCustomName, classes, onEditModeChange, onUploaded, onRemoved }) => {
  const [isEditing, setIsEditing] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 'auto',
    position: 'relative',
  };

  const onChangeCustomName = (e) => {
    onEditCustomName({
      value: e.target.value,
      listIndex,
    });
  };

  const switchEditingMode = () => {
    onEditModeChange(!isEditing);
    setIsEditing(!isEditing);
  };

  return (
    <ListItem ref={setNodeRef} style={style} ContainerComponent="div" dense disabled={!published} sx={classes.listItem}>
      {icon_url ?
        <img className={'ly-flag-icon'} src={icon_url} style={{width: 21, flexShrink: 0}} />
      :
        <i className={'ly-flag-icon'} style={{backgroundImage: `url('/flag-icons/${iso_code}.svg')`, width: 21, flexShrink: 0}} />
      }
      <FileUploadDialog
        onUploaded={onUploaded}
        onRemoved={onRemoved}
        itemKey={itemKey}
        iconUrl={icon_url}
      />
      <ListItemText
        primary={!isEditing ? text : (
          <TextField
            id="standard-bare"
            sx={classes.textField}
            defaultValue={text}
            margin="dense"
            variant="standard"
            onChange={onChangeCustomName}
            fullWidth />
        )}
      />
      <ListItemSecondaryAction style={{display: 'flex', right: 0}}>
        {isEditing &&
          <IconButton
            onClick={switchEditingMode}
            style={styles.toggleEditModeBtn}
            className={'toggleEditModeBtn'}
            size="large">
            <DoneOutlined />
          </IconButton>
        }

        {!isEditing &&
          <IconButton
            onClick={switchEditingMode}
            style={styles.toggleEditModeBtn}
            className={'toggleEditModeBtn'}
            size="large">
            <EditOutlined />
          </IconButton>
        }
        <ListItemIcon
          ref={undefined}
          {...attributes}
          {...listeners}
          className={'toggleEditModeBtn'}
          style={{marginRight: 0, minWidth: 'auto', cursor: 'ns-resize', alignItems: 'center', justifyContent: 'right', touchAction: 'none'}}
        >
          <DragHandleIcon />
        </ListItemIcon>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const SortableList = (props) => {
  const { customIcons } = props;
  const classes = styles;
  const [items, setItems] = useState(props.data);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setItems(props.data);
  }, [props.data, customIcons]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getItemId = (item, index) => `${item.id ?? item.iso_code}-${index}`;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item, index) => getItemId(item, index) === active.id);
    const newIndex = items.findIndex((item, index) => getItemId(item, index) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);
    props.onChange(newItems);
  };

  const onEditCustomName = (data) => {
    let newItems = items.slice();
    newItems[data.listIndex].custom_name = data.value;
    props.onChange(newItems);
  };

  const onEditModeChange = (editing) => {
    setIsEditing(editing);
  };

  const onUploaded = (url) => {
    console.log(url);
  };

  const itemIds = items.map((item, index) => getItemId(item, index));

  const getCustomIconIfExisting = (key) => customIcons[key] || null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <List id="sortableListWrapper" component="div" sx={{...classes.root, padding: 0}}>
          {items.map((item, index) => {
            const { id, iso_code, name, custom_name, published } = item;
            return (
              <SortableItem
                key={getItemId(item, index)}
                id={getItemId(item, index)}
                listIndex={index}
                text={custom_name ? custom_name : name}
                iso_code={iso_code}
                icon_url={getCustomIconIfExisting(iso_code)}
                published={published}
                onUploaded={props.onUploaded}
                onRemoved={props.onRemoved}
                onEditCustomName={onEditCustomName}
                onEditModeChange={onEditModeChange}
                classes={classes}
                itemKey={iso_code}
              />
            );
          })}
        </List>
      </SortableContext>
    </DndContext>
  );
};

const styles = {
  root: {
    minWidth: 'auto !important',
    '& .list-item:hover': {
    }
  },
  listItem: {
    minWidth: 'auto !important',
    padding: '2px 0',
    '& .toggleEditModeBtn': {
      //opacity: 0,
    },
    '&:hover button': {
      backgroundColor: 'grey !important',
      opacity: 1,
    }
  },
  textField: {
    fontSize: 13,
    margin: 0,
    '& .MuiInput-input': {
      padding: 0,
      fontSize: 13
    },
    '& .MuiInput-root': {
      marginTop: 0
    }
  },
  toggleEditModeBtn: {
    float: 'left',
    padding: 2
  },
  '@global': {
    '&.list-item:hover': {
      backgroundColor: 'lightgrey !important',
    }
  },
};

export default SortableList;
